import { checkContactEmailSyntax, extractEmailDomain } from '../utils/contactEmailSyntax';
import { dnsLookupService } from './dnsLookupService';
import type { EmailIssueReason } from './contactEmailScanService';

export type EmailValidationIssue = EmailIssueReason;

export interface EmailValidationResult {
  valid: boolean;
  issues: EmailValidationIssue[];
  message: string | null;
}

const ISSUE_MESSAGES: Record<EmailValidationIssue, string> = {
  invalid_format: 'Please enter a valid email address',
  disposable_domain: 'Disposable email addresses are not allowed',
  no_mx: 'This domain does not appear to accept email',
  dns_error: 'Could not verify this domain',
};

/** Fast client-side checks: format, length, disposable domains. */
export function validateEmailSyntax(email: string): EmailValidationResult {
  const trimmed = email.trim();
  if (!trimmed) {
    return {
      valid: false,
      issues: ['invalid_format'],
      message: 'Email address is required',
    };
  }

  const syntax = checkContactEmailSyntax(trimmed);
  if (syntax === 'ok') {
    return { valid: true, issues: [], message: null };
  }

  const issue = syntax as EmailValidationIssue;
  return {
    valid: false,
    issues: [issue],
    message: ISSUE_MESSAGES[issue],
  };
}

/** Full validation using free DNS-over-HTTPS (Google) for MX records. */
export async function validateEmailAddress(
  email: string,
  options: { checkMx?: boolean } = {}
): Promise<EmailValidationResult> {
  const { checkMx = true } = options;
  const syntaxResult = validateEmailSyntax(email);
  if (!syntaxResult.valid) return syntaxResult;

  if (!checkMx) return syntaxResult;

  const domain = extractEmailDomain(email);
  if (!domain) {
    return {
      valid: false,
      issues: ['invalid_format'],
      message: ISSUE_MESSAGES.invalid_format,
    };
  }

  try {
    const mxResult = await dnsLookupService.lookupMX(domain);
    if (!mxResult.success) {
      return {
        valid: false,
        issues: ['dns_error'],
        message: ISSUE_MESSAGES.dns_error,
      };
    }
    if (mxResult.records.length === 0) {
      return {
        valid: false,
        issues: ['no_mx'],
        message: ISSUE_MESSAGES.no_mx,
      };
    }
    return { valid: true, issues: [], message: null };
  } catch {
    return {
      valid: false,
      issues: ['dns_error'],
      message: ISSUE_MESSAGES.dns_error,
    };
  }
}

export function getEmailValidationMessage(issues: EmailValidationIssue[]): string | null {
  if (issues.length === 0) return null;
  return ISSUE_MESSAGES[issues[0]] ?? ISSUE_MESSAGES.invalid_format;
}
